import React, {FC, useEffect, useState} from 'react';
import {DragDropContext, Droppable, Draggable, DropResult} from 'react-beautiful-dnd';
import {AccessibilityUnit} from "../../../../../../../../../../models/AccessibilityUnits";
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  makeStyles,
  Typography,
} from "@material-ui/core";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {CheckboxGradient} from "../../../../../../../../../components/CheckboxGradient";
import styled from "@emotion/styled";
import {updateUnit} from "../../../../../../../../../../redux/actions/unitsActions";
import {useDispatch} from "react-redux";
import {useSelector} from "../../../../../../../../../../redux/store";
import {AppStateType} from "../../../../../../../../../../redux/reducers";
import CompositeUnits from "./components/CompositeUnits";
import {ExpandMore as ExpandMoreIcon} from "@material-ui/icons";
import {arrayMove, getTextByCurrentLanguage} from "../../../../../../../../../../utils/functions";

const Wrapper = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 73vh;
  padding-right: 10px;
`

type UnitItemProps = {
  borderColor?: string
}

const UnitItemWrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 10px;
  transition: border 0.2s;
  background: rgb(32, 45, 63);
  border: 1px solid rgba(238, 238, 238, 0.04);
  width: 100%;
`

const UnitItem = styled.div<UnitItemProps>`
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 10px;
  //background: rgba(35, 49, 69, 1);
  background: rgb(37 52 73);
  border: 1px solid ${(props) => props.borderColor ? props.borderColor : 'rgba(238, 238, 238, 0.1)'};
`

const Order = styled.div`
  font-size: 16px;
  min-width: 35px;
  text-align: center;
  color: #dadada;
`

const useStyles = makeStyles({
  selectedText: {
    backgroundImage: 'linear-gradient(90deg, #84FAB0 0%, #8FEAFE 100%)',
    '-webkit-background-clip': 'text',
    color: 'transparent',
  },
  selectedIcon: {
    color: '#86f7be'
  },
})

const Accordion = styled(MuiAccordion)`
  border-radius: 10px !important;
  width: 100%;
  background: unset;
  .MuiAccordionSummary-root {
    padding: 0;
  }
  .MuiAccordionSummary-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0;
  }
  .MuiAccordionDetails-root {
    padding: 0;
  }
  .Mui-expanded {
    background-color: unset;
    border-radius: 10px 10px 0 0;
  }
  .MuiAccordionSummary-expandIcon {
    position: absolute;
    right: 55px;
  }
  &::before {
    background-color: unset;
  }
`

const AccordionWrapper = styled.div`
  margin-top: 10px; 
`

type PropsType = {
  selectedUnit: any
  onSelectUnit: (unit: any) => void
  onShowGeneralImageEditor: (status: boolean) => void
}

const UnitList: FC<PropsType> = ({ selectedUnit, onSelectUnit, onShowGeneralImageEditor }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.authReducer.user);
  const units: AccessibilityUnit[] = useSelector((state: AppStateType) => state.unitsReducer.units);
  const [localUnits, setLocalUnits] = useState<AccessibilityUnit[]>([])
  const [expanded, setExpanded] = useState<{ state: Array<{ id: string, state: false }> }>(
    {state: []}
  )

  useEffect(() => {
    if (localUnits.length === 0 && units.length > 0) {
      let reorderingUnits = units
      if (units.every((u) => u.order === units[0].order)) {
        reorderingUnits = reorderingUnits.map((i, index) => ({...i, order: index}))
      }

      const sortedUnits = reorderingUnits.sort((a: any,b: any) => a.order - b.order)
      setLocalUnits(sortedUnits)
      setExpanded({
        ...expanded,
        state: sortedUnits
          .filter((i) => i.related_to && i.related_to.length === 0)
          .map((i) => ({
          id: i.id,
          state: false
        }))
      })
    }
  }, [units]);

  const handleUnitsDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    const parentUnits = localUnits.filter((unit) => unit.related_to && unit.related_to.length === 0);
    const updatedLocalUnits = arrayMove([...parentUnits], startIndex, endIndex);

    updatedLocalUnits.forEach((item, index) => {
      item.order = index;
    });

    const mergedUnits = updatedLocalUnits.flatMap((updatedLocalUnit, index: number) => {
      const relatedUnits = localUnits.filter((unit) => unit.related_to.includes(updatedLocalUnit.id));
      return [updatedLocalUnit, ...relatedUnits];
    }).map((unit, index: number) => ({ ...unit, order: index }));

    setLocalUnits(mergedUnits);

    mergedUnits.forEach((unit) => {
      const foundUnitIndex = units.findIndex((i) => i.id === unit.id);
      dispatch(updateUnit({ order: unit.order }, user!.token, unit.id, foundUnitIndex));
    });
  };

  const handleUnitCheckbox = (unit: AccessibilityUnit) => {
    const newStateLocalUnits = localUnits.map((u) => {
      if (unit.related_to.length > 0 && !unit.is_active && unit.related_to.includes(u.id)) {
        return {
          ...u,
          is_active: true
        }
      }
      if (u.id === unit.id || u.related_to && u.related_to.includes(unit.id)) {
        return {
          ...u,
          is_active: !unit.is_active
        }
      }
      return u
    })

    setLocalUnits(newStateLocalUnits)

    if (unit.related_to.length === 0) { // if we click only on parent unit
      const compositeUnits = getCompositeUnits(unit.id, localUnits)
      if (compositeUnits.length > 0) {
        compositeUnits.forEach((compositeUnit) => {
          const foundUnitIndex = units.findIndex((i) => i.id === compositeUnit.id)
          dispatch(updateUnit({is_active: !unit.is_active}, user!.token, compositeUnit.id, foundUnitIndex))
        })
      }
    }

    const findParent = newStateLocalUnits.find((u) => unit.related_to.includes(u.id))
    if (findParent && !unit.is_active) {
      const foundUnitIndex = units.findIndex((i) => i.id === findParent.id)
      dispatch(updateUnit({is_active: true}, user!.token, findParent.id, foundUnitIndex))
    }

    const foundUnitIndex = units.findIndex((i) => i.id === unit.id)
    if (foundUnitIndex !== -1) {
      dispatch(updateUnit({is_active: !unit.is_active}, user!.token, unit.id, foundUnitIndex))
    }

  }

  const getCompositeUnits = (id: string, units: AccessibilityUnit[]) => {
    if (units.length > 0) {
      const filteredRelatedUnits = units.filter((i) => i.related_to && i.related_to.includes(id))
      if (filteredRelatedUnits.length > 0) {
        return filteredRelatedUnits
      }
    }
    return []
  }

  const handlerExpanded = (id: string): void => {
    setExpanded({ ...expanded, state: expanded.state.map((i: any) => {
        if (i.id === id) {
          return {
            ...i,
            state: !i.state
          }
        }
        return i
      }) })
  }

  const isExpanded = (id: string) => {
    const findExpandedObject = expanded.state.find((i) => i.id === id)
    return findExpandedObject ? findExpandedObject.state : false
  }

  return (
    <Wrapper>
      <UnitItemWrapper>
        <Order style={{color: 'rgba(255, 255, 255, 0.3)'}}>1</Order>
        <UnitItem borderColor={''}>
          <Box pl={3}>
            <DragIndicatorIcon style={{color: 'rgba(255, 255, 255, 0.3)'}}/>
          </Box>
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={'100%'}
            style={{cursor: 'pointer', padding: '15px 10px'}}
            onClick={() => onShowGeneralImageEditor(true)}
          >
            <Typography style={{color: 'rgba(255, 255, 255, 0.3)'}}>
              General Image
            </Typography>
          </Box>

          <Box pr={2}>
            <CheckboxGradient
              checked={true}
              disabled={true}
              onChange={() => {}}
            />
          </Box>
        </UnitItem>
      </UnitItemWrapper>
      <DragDropContext onDragEnd={(result) => handleUnitsDragEnd(result)}>
        <Droppable droppableId="units-list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {localUnits
                .filter((i) => i.related_to && i.related_to.length === 0)
                .map((item: any, index: number) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided, snapshot) => (
                    <AccordionWrapper
                      key={index}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <Accordion
                        expanded={isExpanded(item.id)}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon style={{display: getCompositeUnits(item.id, localUnits).length > 0 ? 'block' : 'none'}}/>}
                          aria-controls={`panel${index + 1}a-content`}
                          id={`panel${index}a-header`}
                          IconButtonProps={{
                            onClick: () => handlerExpanded(item.id)
                          }}
                        >
                          <UnitItemWrapper>
                            <Order style={{color: selectedUnit?.id === item.id ? '#86f7be': '#dadada'}}>{index + 2}</Order>
                            <UnitItem borderColor={selectedUnit?.id === item.id ? '#86f7be' : ''}>
                              <Box {...provided.dragHandleProps} pl={3} className={selectedUnit?.id === item.id ? classes.selectedIcon : ''}>
                                <DragIndicatorIcon />
                              </Box>
                              <Box
                                display={'flex'}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                width={'100%'}
                                style={{cursor: 'pointer', padding: '15px 10px'}}
                                onClick={() => onSelectUnit(item)}
                              >
                                <Typography
                                  className={selectedUnit?.id === item.id ? classes.selectedText : ''}
                                >
                                  {item.title} - {getTextByCurrentLanguage(item.name)}
                                </Typography>
                              </Box>
                              <Box pr={2} pl={7}>
                                <CheckboxGradient
                                  checked={item.is_active}
                                  onChange={() => handleUnitCheckbox(item)}
                                />
                              </Box>
                            </UnitItem>
                          </UnitItemWrapper>
                        </AccordionSummary>
                        <AccordionDetails>
                          <CompositeUnits
                            id={item.id}
                            localUnits={localUnits}
                            selectedUnit={selectedUnit}
                            onSelectUnit={onSelectUnit}
                            onHandleUnitCheckbox={handleUnitCheckbox}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </AccordionWrapper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Wrapper>
  );
}

export default UnitList
